/* eslint-disable sonarjs/cognitive-complexity */
import { FC } from 'react'
import classNames from 'classnames'
import { Button } from '@/atoms/Button'
import { FlexColumn, FlexRow } from '@/atoms/FlexContainers'
import { CheckIcon } from '@/atoms/Icons/CheckIcon'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { CopyIcon } from '@/atoms/Icons/CopyIcon'
import { Image } from '@/atoms/Image'
import { AsDiv, CaptionSM, EyebrowMD, EyebrowSM, HeadingLG, HeadingXS, TitleMD } from '@/atoms/Text'
import { Modal } from '@/molecules/Modal'
import { ValidEarlyBirdDiscount } from '@/services/Theatrical/queries'
import { useTranslate } from '@/utils/translate/translate-client'
import { FlexibleBookingToolTip } from '@/views/TicketCheckoutViews/ShowtimesView/components/ToolTips/FlexibleBookingToolTip'
import { SpecificTimesToolTip } from '@/views/TicketCheckoutViews/ShowtimesView/components/ToolTips/SpecificTimesToolTip'
import TimeLeft from '@/views/TicketCheckoutViews/ShowtimesView/components/banners/DiscountBanner/TimeLeft'
import { PromoImage } from '@/views/TicketCheckoutViews/components/TheatricalPromotionContext/types'
import { useRestrictionCopy } from '@/views/TicketCheckoutViews/components/TheatricalPromotionContext/utils'
import useCopyCode from '../../hooks/useCopyCode'

interface Props {
  onClose: () => void
  open: boolean
  backgroundImage: PromoImage
  accentColor: string
  earlyBirdDiscount: ValidEarlyBirdDiscount
  openingWeek: string
  code: string
}

const EarlyBirdModal: FC<Props> = ({
  open,
  onClose,
  backgroundImage,
  accentColor,
  earlyBirdDiscount,
  openingWeek,
  code,
}) => {
  const { t } = useTranslate('tickets')
  const restrictionCopy = useRestrictionCopy(earlyBirdDiscount, openingWeek)
  const { copiedCode, handleCopyCode } = useCopyCode(code)

  return (
    <Modal
      backgroundScreenColor="black"
      isOpen={open}
      onClose={onClose}
      panelClasses="!bg-core-gray-950 w-full max-w-[380px] shadow-dark-4 rounded-3xl relative text-center"
      transition="appear"
      className="z-[99999]"
    >
      <Image src={backgroundImage.url} fill className="absolute object-center" alt="" quality={50} />
      <div className="text-white">
        <div className="absolute right-6 top-6 z-20 cursor-pointer rounded-full bg-white/10 p-1" onClick={onClose}>
          <CloseIcon color="white" size={18} />
        </div>
        <FlexColumn className="relative z-10 gap-6">
          <FlexColumn className="relative z-20 shrink-0 items-center gap-4">
            <HeadingXS weight="bold">{t('buyEarlySaveBig', 'Buy Early. Save Big.')}</HeadingXS>

            <div>
              <HeadingLG className="uppercase" weight="bold">
                {t('percentOff', '{{percent}}% off', {
                  percent: Math.round(earlyBirdDiscount.activeChildDiscount.value * 100),
                })}
              </HeadingLG>
              <FlexColumn className="items-center gap-0.5">
                <FlexRow className="gap-1">
                  <EyebrowMD as={AsDiv} className="pl-2 text-[0.938rem] uppercase">
                    {t('useCode', 'Use Code')}
                  </EyebrowMD>
                  <div
                    className={classNames('relative flex h-[26px] items-center justify-center overflow-hidden px-1')}
                  >
                    <EyebrowMD
                      as={AsDiv}
                      weight="bold"
                      className="relative z-10 p-1 text-[0.938rem] uppercase"
                      style={{ backgroundColor: accentColor }}
                    >
                      {code}
                    </EyebrowMD>
                  </div>
                </FlexRow>
                <EyebrowMD className="text-[0.938rem] uppercase"></EyebrowMD>
              </FlexColumn>
            </div>
          </FlexColumn>

          <FlexColumn className="relative z-30 gap-1">
            {restrictionCopy && (
              <FlexRow className="mb-3 justify-center gap-1">
                <SpecificTimesToolTip textColor="white" buttonColor="white" copy={restrictionCopy}>
                  <TitleMD weight="bold">{t('onSelectShowtimes', 'On Select Showtimes')}</TitleMD>
                </SpecificTimesToolTip>
              </FlexRow>
            )}

            <FlexColumn className="relative mx-auto w-full max-w-[177px] rounded-2xl px-3 pb-0 pt-2">
              <EyebrowSM className="absolute -top-2 left-1/2 -translate-x-1/2 px-1 uppercase">
                {t('endingIn', 'Ending In')}
              </EyebrowSM>
              <TimeLeft className="gap-4 border-white/30" />
            </FlexColumn>
            <FlexibleBookingToolTip
              className="mx-auto w-fit pt-2"
              panelPosition="CENTER"
              buttonColor="core-gray-400"
              buttonClassName="backdrop-blur border !border-core-gray-800 w-full opacity-60 bg-none"
            />
          </FlexColumn>
          <FlexColumn className="items-center gap-3">
            <Button onClick={handleCopyCode} className="flex w-fit items-center gap-1" variant="white">
              {copiedCode ? <CheckIcon size={24} color="black" /> : <CopyIcon size={24} color="black" />}
              {copiedCode ? t('copiedCode', 'Copied Code') : t('copyCode', 'Copy Code')}
            </Button>
            <CaptionSM italic color="core-gray-400">
              {t('availableForAngelPartneringTheaters', 'Available for Angel Partnering Theaters.')}
            </CaptionSM>
          </FlexColumn>
        </FlexColumn>
      </div>
    </Modal>
  )
}

export default EarlyBirdModal
